import React, { useState, useEffect } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Grow from '@mui/material/Grow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { TModal } from './types.d';

export const TransitionsModal: React.FC<TModal> = ({
  state = false,
  title = '',
  children,
  width,
  height,
  overflow,
  handleCloseModal,
  disabledClose = true,
}) => {
  const [open, setOpen] = useState(state);

  useEffect(() => {
    setOpen(state);
  }, [state]);

  const style = {
    // Centrado puro: no lleva animación, así que su `transform` nunca
    // compite con el `transform: scale(...)` que Grow aplica al Box interno.
    position: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      boxSizing: 'border-box',
      // `display: flex` hace que el Box interno se estire (align-items: stretch)
      // hasta el alto realmente disponible. Con `height: 100%` sobre un padre en
      // `height: auto` (aunque tenga maxHeight), el porcentaje no tiene contra qué
      // resolverse y el hijo termina creciendo según su contenido, rompiendo el
      // scroll interno y los `position: sticky` de las vistas que usan este modal.
      display: 'flex',
      width: width || 400,
      minWidth: 320,
      maxWidth: '92vw',
      height: 'auto',
      maxHeight: height || '90vh',
    },
    modal: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: 'auto',
      // Necesario para que, al ser también un contenedor flex, pueda encogerse
      // por debajo de su contenido en vez de forzar el tamaño mínimo automático.
      minHeight: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      borderRadius: '10px',
      boxShadow: '0 20px 48px rgba(15, 23, 42, 0.28)',
      p: '20px 24px',
      border: 'none',
      outline: 'none',
    },
  };
  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        sx={{ wordWrap: 'break-word', height: 'auto' }}
        open={open}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: { enter: 300, exit: 200 },
          sx: { backgroundColor: 'rgba(15, 23, 42, 0.55)' },
        }}>
        <Box sx={style.position}>
          <Grow
            in={open}
            timeout={{ enter: 300, exit: 200 }}
            easing={{
              enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
              exit: 'ease-in',
            }}>
            <Box sx={style.modal}>
              {disabledClose ? (
                <IconButton
                  aria-label="Cerrar"
                  size="small"
                  onClick={() => {
                    setOpen(false);
                    handleCloseModal(false);
                  }}
                  sx={{
                    position: 'absolute',
                    top: '14px',
                    right: '16px',
                    padding: '4px',
                    zIndex: 2,
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'background.paper',
                    },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
              <Box
                sx={{
                  width: '100%',
                  minHeight: 0,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>
                {title ? (
                  <Typography
                    id="transition-modal-title"
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 2, pr: disabledClose ? '32px' : 0 }}>
                    {title}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    pr: '10px',
                    overflowY: overflow || 'auto',
                    overflowX: 'hidden',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(15, 23, 42, 0.22)',
                      borderRadius: '10px',
                      border: '3px solid transparent',
                      backgroundClip: 'border-box',
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.35)',
                      },
                    },
                  }}>
                  {children}
                </Box>
              </Box>
            </Box>
          </Grow>
        </Box>
      </Modal>
    </div>
  );
};
